import * as React from 'react';
import { getRandomUuidV4 } from "../../common";
import { FileUploadStatus, FileValidationService } from "../../file";
import { IntelligentPhotoCaptureAssignmentSummaryStatus, IntelligentPhotoCaptureAttachmentIterationStatus, IntelligentPhotoCaptureEstimatorCaptureMode } from '../enums';
import { attachmentInfo, attachmentIteration, buildDamageDetectionRequest, copyAsAttachmentInfo, resolveDuplicateContentTypeImages } from '../utilities';
import { useDamageCompletionTimer } from './useDamageCompletionTimer';
import { usePhotoCaptureIterationWorker } from './usePhotoCaptureIterationWorker';
export const usePhotoCaptureEstimatorWorker = ({ attachmentBatchActivityInfo, attachmentIterationInfo, companyCode, contextId, estimatedSummaryFilesInfo, fileSizeLimit, imageMaxWidth, instanceReferenceId, maxFileCount, questionSubType, signalRConnectionId, isSignalRConnected, isSignalRConnectionFailed, tempAttachmentInfo, userVehicleInfo, vehicle, estimatorConfiguration, isPolling, maxPhotoLimitForBatch, onAddAttachmentIteration, onDeleteAllAttachmentIterations, onEstimatorUpload, onInvalidFileUpload, onRequestDamageConnection, onRequestDamageDetection, onUpdateIterationDamageCallStatus, onResetInvalidFileUpload, onTakePhotoUploadSuccess, onUpdateCameraFileStatus, onUpdateIterationBatchCount, onUpdateIterationStatus, onUpdateUserVehicleInfo, photoCaptureDeleteAttachmentsAction, photoCaptureUploadAttachmentsAction }) => {
    const processedCompletedIterationIdsRef = React.useRef(new Set());
    const pendingDamageDetectionIterationIdsRef = React.useRef(new Set());
    const isConnectionRequestInFlightRef = React.useRef(false);
    const [isMaxFileCountModalOpen, setIsMaxFileCountModalOpen] = React.useState(false);
    const [uploadClickCount, setUploadClickCount] = React.useState(0);
    const [albums, setAlbums] = React.useState([]);
    const [albumDocIds, setAlbumDocIds] = React.useState([]);
    const [isSelectModeOn, setIsSelectModeOn] = React.useState(false);
    const [selectedAttachmentIds, setSelectedAttachmentIds] = React.useState([]);
    const [clearSelectionTrigger, setClearSelectionTrigger] = React.useState(0);
    const currentIteration = attachmentIterationInfo.reduce((maxIterationId, iteration) => Math.max(maxIterationId, iteration.iterationId), 0);
    const closeMaxFileCountModal = () => setIsMaxFileCountModalOpen(false);
    const onAlbumOrganizerData = React.useCallback((albumData) => {
        setAlbums(albumData.albums);
        setAlbumDocIds(albumData.albumDocIds);
    }, []);
    const onSelectModeChange = React.useCallback((selectModeOn) => {
        setIsSelectModeOn(selectModeOn);
    }, []);
    const onResetSelection = React.useCallback(() => {
        setSelectedAttachmentIds([]);
        setClearSelectionTrigger((prev) => prev + 1);
    }, []);
    const onTileClick = React.useCallback((tileId, isSelected) => {
        if (!isSelectModeOn) {
            return;
        }
        const matchingTile = albumDocIds.find((tileData) => Number(tileData.tileId) === Number(tileId));
        const attachmentIdentifier = matchingTile === null || matchingTile === void 0 ? void 0 : matchingTile.attachmentIdentifier;
        if (attachmentIdentifier === null || attachmentIdentifier === undefined) {
            return;
        }
        setSelectedAttachmentIds((prev) => {
            if (isSelected) {
                if (prev.includes(attachmentIdentifier)) {
                    return prev;
                }
                return [...prev, attachmentIdentifier];
            }
            return prev.filter((id) => id !== attachmentIdentifier);
        });
    }, [albumDocIds, isSelectModeOn]);
    React.useEffect(() => {
        if (!albums.length) {
            setIsSelectModeOn(false);
            onResetSelection();
        }
    }, [albums, onResetSelection]);
    const handleUploadContextAttachment = (iteration) => {
        let sanitizedAttachments;
        if (iteration.captureMode === IntelligentPhotoCaptureEstimatorCaptureMode.UPLOAD_FILES) {
            sanitizedAttachments = attachmentInfo.addAdditionalArtifactsFolderInfo(iteration, attachmentBatchActivityInfo, tempAttachmentInfo);
        }
        else {
            const updatedAttachments = resolveDuplicateContentTypeImages(tempAttachmentInfo, vehicle, iteration, attachmentBatchActivityInfo, estimatorConfiguration === null || estimatorConfiguration === void 0 ? void 0 : estimatorConfiguration.vinOption);
            const vehicleInfoAttachment = attachmentInfo.getVehicleInfoFromAttachments(updatedAttachments);
            if (vehicleInfoAttachment === null || vehicleInfoAttachment === void 0 ? void 0 : vehicleInfoAttachment.estimatorVehicleInfo) {
                onUpdateUserVehicleInfo(vehicleInfoAttachment.estimatorVehicleInfo);
            }
            sanitizedAttachments = attachmentInfo.updateAttachmentsWithThumbnails(updatedAttachments, estimatedSummaryFilesInfo);
        }
        photoCaptureUploadAttachmentsAction(sanitizedAttachments, contextId, companyCode, iteration.iterationId);
    };
    const handleDeleteSelectedAttachments = (selectedAttachmentIds) => {
        const selectedIdsSet = new Set(selectedAttachmentIds);
        const attachmentsToDelete = [];
        const allAttachments = tempAttachmentInfo.flat();
        for (const attachment of allAttachments) {
            if (selectedIdsSet.has(attachment.id)) {
                attachmentsToDelete.push({
                    id: attachment.id,
                    fileId: attachment.fileId
                });
                if (attachmentsToDelete.length === selectedIdsSet.size) {
                    break;
                }
            }
        }
        if (attachmentsToDelete.length > 0) {
            photoCaptureDeleteAttachmentsAction(attachmentsToDelete, contextId, companyCode, IntelligentPhotoCaptureAssignmentSummaryStatus.DELETE);
            if (attachmentsToDelete.length === allAttachments.length) {
                onDeleteAllAttachmentIterations();
            }
        }
    };
    const handleIdentifySelectedAttachments = (selectedAttachmentIds, destinationFolderTypeCode) => {
        const updatedAttachmentInfo = attachmentInfo.reidentifySelectedAttachmentsByFolderTypeCode(tempAttachmentInfo, selectedAttachmentIds, destinationFolderTypeCode);
        if (updatedAttachmentInfo.length > 0) {
            photoCaptureUploadAttachmentsAction(updatedAttachmentInfo, contextId, companyCode, undefined, IntelligentPhotoCaptureAssignmentSummaryStatus.MOVE);
        }
    };
    const handleCopySelectedAttachment = (selectedAttachmentId, destinationContentType) => {
        const updatedAttachmentInfo = copyAsAttachmentInfo.copySelectedAttachmentToContentType(tempAttachmentInfo, selectedAttachmentId, destinationContentType);
        if (updatedAttachmentInfo.length > 0) {
            photoCaptureUploadAttachmentsAction(updatedAttachmentInfo, contextId, companyCode, undefined, IntelligentPhotoCaptureAssignmentSummaryStatus.COPY);
        }
    };
    const handleRequestDamageDetection = (iterationId) => {
        if (!isPolling && (!isSignalRConnected || !signalRConnectionId)) {
            pendingDamageDetectionIterationIdsRef.current.add(iterationId);
            // Mark IN_PROGRESS immediately so isAnalyzingIterations stays true while waiting for SignalR.
            onUpdateIterationDamageCallStatus(iterationId, IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS, IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS);
            // Only request a new connection once while negotiation is already in progress.
            if (!isConnectionRequestInFlightRef.current) {
                isConnectionRequestInFlightRef.current = true;
                onRequestDamageConnection();
            }
            return;
        }
        const damageDetectionVehicle = userVehicleInfo !== null && userVehicleInfo !== void 0 ? userVehicleInfo : vehicle;
        const damageDetectionRequest = buildDamageDetectionRequest(iterationId, contextId, attachmentIterationInfo, attachmentBatchActivityInfo, damageDetectionVehicle, instanceReferenceId, signalRConnectionId);
        if (!damageDetectionRequest) {
            // No valid damage payload can be built for this iteration.
            // Mark damage flow as completed so Organizing/Analyzing does not wait forever.
            pendingDamageDetectionIterationIdsRef.current.delete(iterationId);
            onUpdateIterationDamageCallStatus(iterationId, IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED, IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED);
            return;
        }
        if (!isPolling)
            pendingDamageDetectionIterationIdsRef.current.delete(iterationId);
        onUpdateIterationDamageCallStatus(iterationId, IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS, IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS);
        onRequestDamageDetection(companyCode, damageDetectionRequest, instanceReferenceId);
    };
    React.useEffect(() => {
        if (!isPolling) {
            if (!isSignalRConnected || !signalRConnectionId) {
                return;
            }
            // Connection is now live: allow future connection requests if needed.
            isConnectionRequestInFlightRef.current = false;
            if (!pendingDamageDetectionIterationIdsRef.current.size) {
                return;
            }
            Array.from(pendingDamageDetectionIterationIdsRef.current).forEach((iterationId) => {
                handleRequestDamageDetection(iterationId);
            });
        }
    }, [isPolling, isSignalRConnected, signalRConnectionId, attachmentIterationInfo, attachmentBatchActivityInfo]);
    React.useEffect(() => {
        if (isPolling || !isSignalRConnectionFailed) {
            return;
        }
        const iterationIdsToComplete = new Set(Array.from(pendingDamageDetectionIterationIdsRef.current));
        attachmentIterationInfo.forEach((iteration) => {
            if (iteration.damageCallStatus === IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS) {
                iterationIdsToComplete.add(iteration.iterationId);
            }
        });
        if (!iterationIdsToComplete.size) {
            isConnectionRequestInFlightRef.current = false;
            return;
        }
        Array.from(iterationIdsToComplete).forEach((iterationId) => {
            onUpdateIterationDamageCallStatus(iterationId, IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED, IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED);
            pendingDamageDetectionIterationIdsRef.current.delete(iterationId);
        });
        isConnectionRequestInFlightRef.current = false;
    }, [isPolling, isSignalRConnectionFailed, onUpdateIterationDamageCallStatus, attachmentIterationInfo]);
    usePhotoCaptureIterationWorker({
        attachmentIterationInfo,
        attachmentBatchActivityInfo,
        companyCode,
        fileSizeLimit,
        imageMaxWidth,
        isPolling,
        maxFileCount,
        maxPhotoLimitForBatch,
        processedCompletedIterationIdsRef,
        questionSubType,
        handleRequestDamageDetection,
        handleUploadContextAttachment,
        onEstimatorUpload,
        onTakePhotoUploadSuccess,
        onUpdateCameraFileStatus,
        onUpdateIterationBatchCount,
        onUpdateIterationStatus
    });
    useDamageCompletionTimer({
        attachmentIterationInfo,
        onUpdateIterationDamageCallStatus
    });
    const limitFilesToMaxCount = (files) => {
        if (files.length > maxFileCount) {
            setIsMaxFileCountModalOpen(true);
            return files.slice(0, maxFileCount);
        }
        return files;
    };
    const handleSelected = (fileList, captureMode) => {
        setUploadClickCount(prev => prev + 1);
        const files = limitFilesToMaxCount(Array.from(fileList));
        const validFiles = [];
        onResetInvalidFileUpload();
        files.forEach((file) => {
            const fileUploadError = FileValidationService.validateFile({
                maxFileSize: fileSizeLimit,
                allowedFileTypes: attachmentIteration.getAllowedFileTypes(captureMode),
                file
            });
            if (fileUploadError) {
                onInvalidFileUpload({
                    fileId: getRandomUuidV4(),
                    fileName: file.name,
                    fileSize: file.size,
                    uploadError: fileUploadError,
                    uploadStatus: FileUploadStatus.FAILED
                });
                return;
            }
            validFiles.push(file);
        });
        if (!validFiles.length) {
            return;
        }
        createAttachmentIteration(validFiles, captureMode);
    };
    const createCameraIteration = (files, captureMode) => {
        createAttachmentIteration(files, captureMode);
    };
    const createAttachmentIteration = (files, captureMode) => {
        const nextIterationId = attachmentIteration.generateUniqueIterationId(attachmentIterationInfo);
        const newIteration = attachmentIteration.createAttachmentIterationInfo(captureMode, files, nextIterationId, maxPhotoLimitForBatch);
        onAddAttachmentIteration(newIteration);
    };
    return {
        currentIteration,
        isMaxFileCountModalOpen,
        uploadClickCount,
        albums,
        isSelectModeOn,
        selectedAttachmentIds,
        clearSelectionTrigger,
        closeMaxFileCountModal,
        createCameraIteration,
        createAttachmentIteration,
        handleSelected,
        onAlbumOrganizerData,
        onSelectModeChange,
        onResetSelection,
        onTileClick,
        onCopySelectedAttachment: handleCopySelectedAttachment,
        onDeleteSelectedAttachments: handleDeleteSelectedAttachments,
        onIdentifySelectedAttachments: handleIdentifySelectedAttachments
    };
};
//# sourceMappingURL=usePhotoCaptureEstimatorWorker.js.map